import { get, isEmpty, map } from "lodash-es";

import getRegexListingChecker from "../../plugins/helpers/__ts/module-type/module-type.regexListingChecker.plugin";

import { ListingType } from "@/shared/enums/listing-type.enum";

export async function getCheckRestriction(
  app: Record<string, any>,
  path: string,
): Promise<Record<string, any>> {
  const { regexListingChecker } = getRegexListingChecker({} as any);

  let checkRestriction: Record<string, any> = {};

  await (async function handleCheckRestriction(): Promise<void> {
    let result: Record<string, any> = {
      result: false,
    };

    // check based on menu & permission on sidebar
    const hasForbiddenDefinedMenu = !isEmpty(
      (app.$permission.permissionSidebarRoutingChecker(path) || []).find(
        (item: Record<string, any> = {}) =>
          (item.page && !item.permission) ||
          (item.child &&
            (item.child || []).find(
              (itemChild: Record<string, any>) =>
                itemChild.page && !itemChild.permission,
            )),
      ) || {},
    );
    if (hasForbiddenDefinedMenu) {
      result = {
        result: true,
      };
    }

    // product category
    // /<module name> (e.g: health, logistic)
    if (app.store.getters["auth/getUserData"] !== null) {
      const moduleName = app.$helpers.getModule();

      // fetch product industry categories
      // if it's not organisation page (because already fetched in the page)
      // or getters still empty
      if (
        !path.includes("/organisation") &&
        !app.store.getters["organization/getOrganizationsProductCategories"]
          .length
      ) {
        await app.store.dispatch("organization/listProductCategories", {});
      }
      const isProductCategory = map(
        app.store.getters["organization/getOrganizationsProductCategories"],
        (category) => category.slug,
      ).includes(moduleName);
      const { organization } = app.store.getters["auth/getUserData"];
      const { productCategories } = organization;
      if (isProductCategory) {
        if (
          !app.$helpers.isProductIncluded(productCategories, moduleName) &&
          !app.$helpers.isQoalaSuperAdmin()
        ) {
          result = {
            result: true,
          };
        } else if (!app.$helpers.isQoalaSuperAdmin()) {
          // check policy list permission
          if (
            app.$moduleType.regexIsPolicyPage(path) &&
            !app.$permission.checkPermissionPolicyListPage(moduleName)
          ) {
            result = {
              result: true,
            };
          }

          // check policy detail
          // /policy/<detail>
          if (
            app.$moduleType.regexIsPolicyDetailPage(path) &&
            !app.$permission.permissionPolicyDetailPage()
          ) {
            result = {
              result: true,
            };
          }

          // check claim list permission
          if (
            app.$moduleType.regexIsClaimPage(path) &&
            !app.$permission.checkPermissionClaimListPage(moduleName)
          ) {
            result = {
              result: true,
            };
          }

          // check claim detail
          // /claim/<detail>
          if (
            app.$moduleType.regexIsClaimDetailPage(path) &&
            !app.$permission.permissionClaimDetailPage()
          ) {
            result = {
              result: true,
            };
          }
        }
      } else if (app.$moduleType.getModule() === "partnership") {
        if (
          app.$moduleType.regexIsClaimDetailPage(path) &&
          !app.$permission.isAbleToShowPartnershipClaimDetail()
        ) {
          result = {
            result: true,
          };
        } else if (
          app.$moduleType.regexIsPolicyDetailPage(path) &&
          !app.$permission.isAbleToShowPartnershipPolicyDetail()
        ) {
          result = {
            result: true,
          };
        } else if (
          app.$moduleType.regexIsInvoiceDetailPage(path) &&
          !app.$permission.isAbleToShowPartnershipInvoiceMenu()
        ) {
          result = {
            result: true,
          };
        }
      }
    }

    // check mobile path with pathAvailableMobile
    if (app.$ui.isMobileView()) {
      const pathAvailableMobile = [
        "/otp",
        "/forget-password",
        "/change-password",
        "/verify",
        "/finish",
        "/home",
        "/gadget/policy",
        "/gadget/claim",
        "/new-policy",
        "/partnership/policy-create",
        ...// TURN OFF COMMISSION DETAIL & COMMISSION MOBILE FOR RELEASE
        (!app.$helpers.turnOffCommission()
          ? ["/commission", "/payout-history"]
          : []),
      ];

      const isPathIncludedOnMobile = pathAvailableMobile.find(
        (pathMobile) =>
          (path.includes(pathMobile) &&
            pathMobile !== "/commission" &&
            pathMobile !== "/payout-history") ||
          path === "/" ||
          path === "/profile" ||
          path === `/${app.i18n.locale}/profile` ||
          // commission
          (app.$moduleType.regexIsCommissionPage(path) &&
            app.$permission.permissionCommissionPage() &&
            // TURN OFF COMMISSION DETAIL & COMMISSION MOBILE FOR RELEASE
            !app.$helpers.turnOffCommission()) ||
          // payout history
          (regexListingChecker(path, ListingType.PAYOUT_HISTORY) &&
            app.$permission.permissionPayoutHistoryList() &&
            // TURN OFF COMMISSION DETAIL & COMMISSION MOBILE FOR RELEASE
            !app.$helpers.turnOffCommission()),
      );

      // isAccountMobileAvailable
      // (gadget || smartphone) && (partner || service center)
      const isQoalaAdmin = app.$helpers.isQoalaAdmin();
      const isQoalaAreps = app.$helpers.isQoalaAreps();
      const isPartner = app.$helpers.isPartner();
      const isServiceCenter = app.$helpers.isServiceCenter();

      const isAccountMobileAvailable =
        app.store.getters["auth/getUserData"] === null ||
        (app.store.getters["auth/getUserData"] !== null &&
          get(
            app.store.getters["auth/getUserData"],
            "organization.industries",
            [],
          ).length &&
          (get(
            app.store.getters["auth/getUserData"],
            "organization.industries",
            [],
          ).includes("gadget") ||
            get(
              app.store.getters["auth/getUserData"],
              "organization.industries",
              [],
            ).includes("smartphone")) &&
          (isPartner || isServiceCenter || isQoalaAreps || isQoalaAdmin));

      result = {
        result: !(
          typeof isPathIncludedOnMobile !== "undefined" &&
          isPathIncludedOnMobile !== null &&
          isAccountMobileAvailable
        ),
        isPathMobileDisabled: !["/gadget/policy", "/gadget/claim"].includes(
          path,
        ),
        isProductRestricted: result.result,
        isAccountMobileDisabled: !isAccountMobileAvailable,
      };
    }

    checkRestriction = result;
  })();

  return checkRestriction;
}
